import CloseIcon from '@mui/icons-material/Close';
import { FormControl, IconButton, InputLabel, MenuItem, Select, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { GenericParser, useQueryState } from 'nuqs';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useListViewContext } from '../context/ListViewContext';

/**
 * Option for select filter dropdown.
 */
export interface SelectOption {
  /** The value of the option */
  value: string;
  /** Message descriptor for i18n (optional). When set, used with $t(option.message). */
  message?: MessageDescriptor;
  /** Translation key for i18n (optional). Used when message is not set. */
  labelKey?: string;
  /** Default message for translation when using labelKey (optional) */
  defaultMessage?: string;
  /** Raw string label as fallback (optional) */
  label?: string;
}

/**
 * Props for the generic select filter component.
 */
export interface GenericSelectFilterProps {
  /** The URL query parameter key */
  name: string;
  /** Message descriptor for the label */
  label: MessageDescriptor;
  /** Options for the select dropdown */
  options: readonly SelectOption[];
  /** Parser for reading/writing this filter value from/to URL (from FilterDefinition). Handles defaults via .withDefault(). */
  parser: GenericParser<string | null>;
  /** Whether the filter is active (should be shown even if empty) */
  isActive?: boolean;
  /** Callback when filter is cleared (e.g. to remove from active filters set for addable filters) */
  onRemove?: () => void;

  /** Custom styles for the select container */
  sx?: SystemStyleObject<Theme>;
}

/**
 * Generic select filter component with immediate update.
 * Writes go through ListView context (setFilterValue) so filter changes are bundled with a page reset.
 * Reads come from nuqs (useQueryState) for simplicity.
 */
export function GenericSelectFilter({
  name,
  label,
  options,
  parser,
  isActive,
  onRemove,
  sx,
}: GenericSelectFilterProps) {
  const { $t } = useIntl();
  const { setFilterValue } = useListViewContext();

  // Read-only: get current value from URL (parent's useQueryStates manages it)
  const [value] = useQueryState(name, parser);

  // If it's an addable filter with no value and not active, don't render it in the bar
  // (It lives in the "Add Filter" menu until selected). Permanent filters don't receive
  // isActive, so isActive is undefined and we never hide them.
  if (isActive === false && !value) {
    return null;
  }

  const handleChange = (val: string | null) => {
    setFilterValue(name, val);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFilterValue(name, null);
  };

  const hasValue = value != null && value !== '';
  // For permanent filters (isActive === undefined): only show clear when there is a value to clear.
  // For addable filters: always show when onRemove exists (to clear value or remove from bar).
  const showClearIcon = onRemove && (isActive !== undefined || hasValue);

  const translatedLabel = $t(label);

  return (
    <FormControl
      size="small"
      sx={[
        {
          minWidth: '18ch',
        },
        sx ?? {},
      ]}
    >
      <InputLabel>{translatedLabel}</InputLabel>
      <Select
        value={value || ''}
        label={translatedLabel}
        onChange={(e) => handleChange(e.target.value || null)}
        sx={{
          '& .MuiSelect-select': {
            paddingRight: '60px !important', // Make room for both icons
          },
        }}
        endAdornment={
          showClearIcon ? (
            <IconButton
              aria-label={$t({
                id: 'common.action.clear',
                defaultMessage: 'Limpiar',
              })}
              onClick={handleClear}
              onMouseDown={(e) => e.stopPropagation()}
              edge="end"
              size="small"
              sx={{
                position: 'absolute',
                right: '35px', // Sit to the left of the arrow (which is at right: 7px)
                top: 'calc(50% - 14px)', // Center vertically
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined
        }
      >
        <MenuItem value="">
          <em>
            {$t({
              id: 'common.text.all',
              defaultMessage: 'Todos',
            })}
          </em>
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.message ? $t(opt.message) : (opt.label ?? opt.value)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
